import React, { useEffect, useMemo, useState } from 'react';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';

import {
	compositionCalculation,
	compositionDefaultFieldSet,
	onChangeCompositionPercentage
} from '@utils/MaterialsUtil';

import {
	useGetCompositionNameListQuery,
	useGetMaterialConstructionsQuery
} from '../api/MultipleTechpackJourneyApi';

const N = 4;

const partsFromInitial = (initialComposition) => {
	if (!Array.isArray(initialComposition)) return [];
	return initialComposition.slice(0, N).map((row) => {
		const f = row?.fiber;
		const fiberName =
			f == null
				? ''
				: typeof f === 'object'
					? String(f.name ?? f.value ?? '').trim()
					: String(f).trim();
		return { fiberName, percentage: row?.percentage != null ? row.percentage : undefined };
	});
};

const constructionOptionLabel = (item) =>
	[item?.fabricType, item?.groupName, item?.name].filter(Boolean).join('-');

const AddFabricCompositionModal = ({
	onSubmit,
	onClose,
	selectedFabricType,
	selectedConstructionId,
	selectedGsmValue,
	initialComposition
}) => {
	const { data: compRes } = useGetCompositionNameListQuery();
	const { data: constructionRes } = useGetMaterialConstructionsQuery();

	const constructionOptions = Array.isArray(constructionRes) ? constructionRes : [];

	const compositionNameList = useMemo(() => {
		const arr = Array.isArray(compRes) ? compRes : [];
		return arr.map((item) => {
			const label = typeof item === 'string' ? item : String(item?.name ?? item?.value ?? item);
			return { id: label, text: label, value: label, label };
		});
	}, [compRes]);

	const [fiberTypeName, setFiberTypeName] = useState('');
	const [weight, setWeight] = useState('');
	const [remarks, setRemarks] = useState('');
	const [fabricTypeConstant, setFabricTypeConstant] = useState('');
	const [compositionFieldInfoList, setCompositionFieldInfoList] = useState(() =>
		compositionDefaultFieldSet(N, [])
	);

	useEffect(() => {
		setWeight(selectedGsmValue || '');
		if (selectedConstructionId != null && selectedConstructionId !== '') {
			const byId = constructionOptions.find((r) => String(r?.id) === String(selectedConstructionId));
			if (byId?.name) {
				setFiberTypeName(String(byId.name));
				return;
			}
		}
		setFiberTypeName(selectedFabricType || '');
	}, [selectedGsmValue, selectedFabricType, selectedConstructionId, constructionOptions]);

	useEffect(() => {
		setCompositionFieldInfoList(compositionDefaultFieldSet(N, partsFromInitial(initialComposition)));
	}, [initialComposition]);

	const compositionError = useMemo(() => {
		const touched = compositionFieldInfoList.some((r) => r.percentage !== undefined || r.fiberName);
		if (!touched) return '';
		const cal = compositionCalculation(compositionFieldInfoList);
		if (cal.percentageIsNotOk) return 'Total composition needs to be 100%';
		if (cal.fiberNameIsNotOk) return 'Select composition';
		return '';
	}, [compositionFieldInfoList]);

	const selectedConstruction = fiberTypeName
		? constructionOptions.find((r) => String(r?.name ?? '') === String(fiberTypeName))
		: undefined;

	const fabricLabel = fiberTypeName || '';

	const setFiberAt = (item, index) =>
		setCompositionFieldInfoList((list) =>
			list.map((obj, i) => (i === index ? { ...obj, fiberObj: item, fiberName: item?.value } : obj))
		);

	const compositionPayload = () =>
		compositionFieldInfoList
			.filter((row) => row.fiberName || row.percentage !== undefined)
			.map((row) => ({
				fiber: { id: '', name: row.fiberName || '' },
				percentage: row.percentage != null ? String(row.percentage) : '',
				fabricType:
					selectedConstruction?.id != null ? String(selectedConstruction.id) : fiberTypeName || ''
			}));

	const submit = () => {
		if (compositionError) return;
		onSubmit?.({
			fabricTypeCode: selectedConstruction?.id != null ? String(selectedConstruction.id) : '',
			fabricTypeLabel: selectedConstruction?.name || fiberTypeName || '',
			weight,
			fabricTypeConstant,
			remarks,
			composition: compositionPayload()
		});
	};
	return (
		<ModalContainer className="add-fabric-composition-modal" onCloseModal={onClose}>
			<div className="common-popup shadow-24dp">
				<div className="common-popup-body p-3">
					<div className="add-dropdown bg-white w-100">
						<div className="row">
							<div className="col-md-12">
								<div className="form-group mr-12">
									<div className="category-select position-relative">
										<label>Fabric type*</label>
										<CustomDropdown
											title="Select..."
											items={constructionOptions.map((item) => ({
												id: item?.id,
												value: constructionOptionLabel(item)
											}))}
											selectedItem={fabricLabel}
											searchPlaceholder="Select..."
											onItemClick={(item) => {
												const row = constructionOptions.find(
													(r) => String(r?.id) === String(item?.id)
												);
												setFiberTypeName(row?.name ?? '');
												setFabricTypeConstant(row?.fabricTypeConstant ?? '');
											}}
										/>
									</div>
								</div>
							</div>
							<div className="col-md-12">
								<label>Composition*</label>
							</div>
							<div className="col-md-12 compositions-list">
								<div className="row">
									{compositionFieldInfoList.map((obj, index) => (
										<div className="col-md-6" key={`composition-${index}`}>
											<div className="form-group composition-item mb-2">
												<div className="d-flex">
													<input
														type="number"
														defaultValue={obj?.percentage}
														min={0.1}
														max={100}
														onBlur={(e) =>
															onChangeCompositionPercentage(
																e.target.value,
																index,
																compositionFieldInfoList,
																setCompositionFieldInfoList
															)
														}
														className="input-percentage"
													/>
													<p className="percentage">%</p>
													<div className="select-component">
														<div className="form-group mb-0">
															<CustomDropdown
																type="fabricName"
																title="Composition"
																toolTipLength={13}
																onItemClick={(v) => setFiberAt(v, index)}
																items={compositionNameList}
																selectedItem={obj?.fiberName}
																searchPlaceholder="Composition"
															/>
														</div>
													</div>
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
							<div className="col-md-12 compositonError mb-12">
								{compositionError && <p className="error">{compositionError}</p>}
							</div>

							<div className="col-md-12">
								<div className="form-group">
									<label htmlFor="weight-static">Weight*</label>
									<div className="d-flex">
										<input
											id="weight-static"
											type="number"
											placeholder="00"
											value={weight}
											onChange={(e) => setWeight(e.target.value)}
										/>
									</div>
								</div>
							</div>
							<div className="col-md-12">
								<div className="form-group">
									<label htmlFor="remarks-static">Remarks</label>
									<input
										id="remarks-static"
										type="text"
										autoComplete="off"
										placeholder="Remarks"
										value={remarks}
										onChange={(e) => setRemarks(e.target.value)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="modal-footer border-0 pt-0">
					<div className="footer-buttons text-right w-100">
						<div className="d-flex justify-content-end gap-2">
							<button
								type="button"
								className="button text-only text-uppercase"
								onClick={onClose}
							>
								Cancel
							</button>
							<button
								type="button"
								className="button text-only text-uppercase"
								onClick={submit}
							>
								Submit
							</button>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddFabricCompositionModal;
